import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../axios';
import { mapJioSaavnAlbumToSpotifyAlbum, mapJioSaavnPlaylistToSpotifyPlaylist, mapJioSaavnSongToSpotifyTrack, mapJioSaavnShowToSpotifyEpisode, mapJioSaavnEpisodeToSpotifyEpisode } from '../../utils/jiosaavn-adapters';

import type { Track } from '../../interfaces/track';
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';
import type { Episode } from '../../interfaces/episode';
import { fetchMoreLikeArtistItems } from '../../pages/Home/utils/fetchMoreLikeArtistItems';

export interface MoreLikeArtistSection {
  artist: Artist;
  items: Awaited<ReturnType<typeof fetchMoreLikeArtistItems>>;
}

const initialState: {
  topTracks: Track[];
  newReleases: Album[];
  madeForYou: Playlist[];
  featurePlaylists: Playlist[];
  rankings: Playlist[];
  trending: Playlist[];
  trendingSongs: Track[];
  recentlyPlayed: (Track | Artist | Album)[];
  section: 'ALL' | 'MUSIC' | 'PODCAST';
  podcastFilter: 'PODCASTS' | 'FOLLOWING';
  episodesMightLike: Episode[];
  episodesToTry: Episode[];
  moreLikeArtists: MoreLikeArtistSection[];
  recommendedStations: Playlist[];
  isLoading: boolean;
} = {
  trending: [],
  trendingSongs: [],
  rankings: [],
  topTracks: [],
  section: 'ALL',
  podcastFilter: 'PODCASTS',
  madeForYou: [],
  newReleases: [],
  recentlyPlayed: [],
  featurePlaylists: [],
  episodesMightLike: [],
  episodesToTry: [],
  moreLikeArtists: [],
  recommendedStations: [],
  isLoading: true,
};

try {
  const localHistory = localStorage.getItem('recentlyPlayed');
  if (localHistory) {
    initialState.recentlyPlayed = JSON.parse(localHistory);
  }
} catch (e) {}

let modulesPromise: Promise<any> | null = null;
const fetchModules = async () => {
  if (modulesPromise) return modulesPromise;
  modulesPromise = (async () => {
    try {
      const res = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
      const data = res.data?.data;
      
      if (!data) {
        modulesPromise = null;
        return {};
      }

      const trendingSongs = (data.new_trending || []).filter((item: any) => item.type === 'song');
      const trendingPlaylists = (data.new_trending || []).filter((item: any) => item.type === 'playlist');
      const trendingAlbums = (data.new_trending || []).filter((item: any) => item.type === 'album');
      
      const newAlbumsOnly = (data.new_albums || []).filter((item: any) => item.type === 'album');

      return {
        trending: {
          songs: trendingSongs,
          playlists: trendingPlaylists,
          albums: trendingAlbums
        },
        albums: newAlbumsOnly,
        charts: data.charts || [],
        playlists: data.top_playlists || [],
        top_shows: data.top_shows || { shows: [] },
        radio: data.radio ? (data.radio.featured_stations || []) : []
      };
    } catch (e) {
      console.error(e);
      modulesPromise = null;
      return {};
    }
  })();
  return modulesPromise;
};

export const fetchMadeForYou = createAsyncThunk('home/fetchMadeForYou', async () => {
  const modules = await fetchModules();
  return (modules.playlists || []).slice(0, 12).map(mapJioSaavnPlaylistToSpotifyPlaylist);
});

export const fetchRanking = createAsyncThunk('home/fetchRanking', async () => {
  const modules = await fetchModules();
  return (modules.charts || []).map(mapJioSaavnPlaylistToSpotifyPlaylist);
});

export const fetchTrending = createAsyncThunk('home/fetchTrending', async () => {
  const modules = await fetchModules();
  return (modules.trending?.playlists || []).map(mapJioSaavnPlaylistToSpotifyPlaylist);
});

export const fetchTrendingSongs = createAsyncThunk('home/fetchTrendingSongs', async () => {
  const res = await axios.get('/trending');
  return (res.data?.data || []).map(mapJioSaavnSongToSpotifyTrack);
});

export const fetchNewReleases = createAsyncThunk('home/fetchNewReleases', async () => {
  const modules = await fetchModules();
  return (modules.albums || []).map(mapJioSaavnAlbumToSpotifyAlbum);
});

export const fetchTopTracks = createAsyncThunk('home/fetchTopTracks', async () => {
  try {
    const res = await axios.get<any>('/search/songs', { params: { query: 'top hits', limit: 30 } });
    const songs = res.data?.data?.results || [];
    
    const uniqueSongs = [];
    const seenIds = new Set();
    for (const song of songs) {
      const id = song.id;
      if (id && !seenIds.has(id)) {
        seenIds.add(id);
        uniqueSongs.push(song);
      }
      if (uniqueSongs.length >= 8) break;
    }
    
    return uniqueSongs.map(mapJioSaavnSongToSpotifyTrack);
  } catch (e) {
    return [];
  }
});

export const fecthFeaturedPlaylists = createAsyncThunk('home/fecthFeaturedPlaylists', async () => {
  const modules = await fetchModules();
  return (modules.playlists || []).slice(12, 24).map(mapJioSaavnPlaylistToSpotifyPlaylist);
});

export const fetchPodcastEpisodes = createAsyncThunk('home/fetchPodcastEpisodes', async () => {
  try {
    let showList: any[] = [];
    try {
      const modRes = await axios.get('/modules?language=hindi,english');
      if (modRes.data?.data?.top_shows?.length > 0) {
        showList = modRes.data.data.top_shows;
      }
    } catch (e) {}

    const featuredShowIds = ['62078', '64326', '77541', '40305', '3087', '143075'];
    const showIds = Array.from(new Set([
      ...featuredShowIds,
      ...showList.map((s: any) => String(s.id))
    ])).slice(0, 8);

    const showsData = await Promise.all(
      showIds.map(async (id) => {
        try {
          const res = await axios.get(`/shows/${id}`);
          return res.data?.data;
        } catch (e) {
          return null;
        }
      })
    );

    const episodes: any[] = [];
    for (const show of showsData) {
      if (!show || !show.episodes || show.episodes.length === 0) continue;
      const ep = show.episodes[0];
      episodes.push(mapJioSaavnEpisodeToSpotifyEpisode(ep, show));
    }

    if (episodes.length === 0) {
      const res = await axios.get('/search/shows', { params: { query: 'podcast' } });
      const shows = res.data?.data?.results || [];
      const mapped = shows.map(mapJioSaavnShowToSpotifyEpisode);
      const half = Math.ceil(mapped.length / 2);
      return {
        mightLike: mapped.slice(0, half),
        toTry: mapped.slice(half)
      };
    }

    const half = Math.ceil(episodes.length / 2);
    return {
      mightLike: episodes.slice(0, half),
      toTry: episodes.slice(half)
    };
  } catch (e) {
    return { mightLike: [], toTry: [] };
  }
});
export const fetchMoreLikeArtists = createAsyncThunk('home/fetchMoreLikeArtists', async () => []);

export const fetchRecommendedStations = createAsyncThunk('home/fetchRecommendedStations', async () => {
  try {
    const res = await axios.get<any>('/search/songs', { params: { query: 'top hits', limit: 8 } });
    const songs = res.data?.data?.results || [];
    
    // Extract unique artist IDs from songs
    const artistIds = Array.from(new Set(songs.flatMap((s: any) => s.artists?.primary?.map((a: any) => a.id) || [])));
    
    // Fetch similar artists for each to construct the radio station
    const stations = await Promise.all(
      artistIds.slice(0, 6).map(async (id: any) => {
        try {
          // fetch artist details to get similar artists
          const artistRes = await axios.get(`/artists?id=${id}`);
          const artist = artistRes.data.data;
          const similar = artist.similarArtists || [];
          
          return {
            id: artist.id,
            name: `${artist.name || artist.title}`,
            description: `With ${similar.slice(0, 3).map((a: any) => a.name).join(', ')} and more`,
            images: [
              artist.image, 
              ...similar.slice(0, 2).map((a: any) => a.image)
            ].map(img => {
              if (!img) return '';
              if (typeof img === 'string') return img;
              if (Array.isArray(img)) return img[img.length - 1]?.link || img[img.length - 1]?.url || img[img.length - 1];
              return img.link || img.url || img;
            }).map((url: string) => url ? url.replace(/150x150|50x50/g, '500x500') : '').filter(Boolean),
            type: 'radio_station',
            uri: `spotify:radio:${artist.id}`
          };
        } catch (e) {
          return null;
        }
      })
    );
    return stations.filter(Boolean) as any[];
  } catch (e) {
    return [];
  }
});

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setSection(state, action: PayloadAction<'ALL' | 'MUSIC' | 'PODCAST'>) {
      state.section = action.payload;
      if (action.payload !== 'PODCAST') {
        state.podcastFilter = 'PODCASTS';
      }
    },
    setPodcastFilter(state, action: PayloadAction<'PODCASTS' | 'FOLLOWING'>) {
      state.podcastFilter = action.payload;
    },
    addRecentlyPlayed(state, action: PayloadAction<Track | Album | Artist>) {
      const item = action.payload;
      const index = state.recentlyPlayed.findIndex((i) => i.id === item.id);
      if (index > -1) {
        state.recentlyPlayed.splice(index, 1);
      }
      state.recentlyPlayed.unshift(item);
      state.recentlyPlayed = state.recentlyPlayed.slice(0, 20); // Keep max 20
      try {
        localStorage.setItem('recentlyPlayed', JSON.stringify(state.recentlyPlayed));
      } catch (e) {}
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNewReleases.fulfilled, (state, action) => {
      state.newReleases = action.payload as any as any[];
      state.isLoading = false;
    });
    builder.addCase(fetchNewReleases.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(fetchTopTracks.fulfilled, (state, action) => {
      state.topTracks = action.payload;
    });
    builder.addCase(fecthFeaturedPlaylists.fulfilled, (state, action) => {
      state.featurePlaylists = action.payload;
    });
    builder.addCase(fetchMadeForYou.fulfilled, (state, action) => {
      state.madeForYou = action.payload;
    });

    builder.addCase(fetchRanking.fulfilled, (state, action) => {
      state.rankings = action.payload;
    });
    builder.addCase(fetchTrending.fulfilled, (state, action) => {
      state.trending = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchTrending.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(fetchTrendingSongs.fulfilled, (state, action) => {
      state.trendingSongs = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchPodcastEpisodes.fulfilled, (state, action) => {
      state.episodesMightLike = action.payload.mightLike;
      state.episodesToTry = action.payload.toTry;
    });
    builder.addCase(fetchMoreLikeArtists.fulfilled, (state, action) => {
      state.moreLikeArtists = action.payload;
    });
    builder.addCase(fetchRecommendedStations.fulfilled, (state, action) => {
      state.recommendedStations = action.payload;
    });
  },
});

export const homeActions = {
  ...homeSlice.actions,
  fetchRanking,
  fetchTrending,
  fetchTrendingSongs,
  fetchTopTracks,
  fetchMadeForYou,
  fetchNewReleases,
  fecthFeaturedPlaylists,
  fetchPodcastEpisodes,
  fetchMoreLikeArtists,
  fetchRecommendedStations,
};

export default homeSlice.reducer;
